import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  where
} from 'firebase/firestore';
import { db } from './firebase';
import { Promotion, NewPromotion } from '../models/Promotion';
import { adminScopeService } from './adminScopeService';

const PROMOTIONS_COLLECTION = 'promotions';

const convertFirestoreDataToPromotion = (id: string, data: any): Promotion => {
  const startDate =
    data.startDate instanceof Timestamp ? data.startDate.toDate() : new Date(data.startDate);
  const endDate =
    data.endDate instanceof Timestamp ? data.endDate.toDate() : new Date(data.endDate);
  return {
    id,
    active: data.active,
    scopeType: data.scopeType,
    scopeId: data.scopeId,
    description: data.description,
    discountType: data.discountType,
    discountValue: data.discountValue,
    // Nouveau: champs additionnels
    applyOn: data.applyOn,
    freeDelivery: data.freeDelivery === true,
    autoApplyInStepClient: data.autoApplyInStepClient === true,
    startDate,
    endDate,
    usageLimitPerUser: data.usageLimitPerUser,
    usageLimitTotal: data.usageLimitTotal,
    usedCountTotal: data.usedCountTotal,
    minOrderAmount: data.minOrderAmount,
    eligibleProducts: data.eligibleProducts,
    eligiblePhones: data.eligiblePhones,
    createdAt:
      data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined,
    updatedAt:
      data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined
  };
};

const promotionMatchesCurrentStoreScope = (promotion: Promotion): boolean => {
  const currentStoreId = adminScopeService.getCurrentStoreId();
  if (!currentStoreId) {
    return true;
  }

  return promotion.scopeType === 'store' && promotion.scopeId === currentStoreId;
};

const assertPromotionInCurrentStoreScope = (
  promotion: Promotion | null,
  id: string,
  action: string
): Promotion => {
  if (!promotion) {
    throw new Error(`Code promo ${id} introuvable pour ${action}.`);
  }

  if (!promotionMatchesCurrentStoreScope(promotion)) {
    throw new Error(`Le code promo ${id} est hors du scope magasin actif.`);
  }

  return promotion;
};

const promotionService = {
  // Récupérer tous les codes promo, triés par date de début descendante
  getPromotions: async (): Promise<Promotion[]> => {
    const promotionsRef = collection(db, PROMOTIONS_COLLECTION);
    const currentStoreId = adminScopeService.getCurrentStoreId();
    const snapshot = currentStoreId
      ? await getDocs(
          query(
            promotionsRef,
            where('scopeType', '==', 'store'),
            where('scopeId', '==', currentStoreId)
          )
        )
      : await getDocs(promotionsRef);
    let promotions = snapshot.docs.map((docSnap) =>
      convertFirestoreDataToPromotion(docSnap.id, docSnap.data())
    );
    promotions.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
    return promotions;
  },

  // Récupérer un code promo par son ID
  getPromotionById: async (id: string): Promise<Promotion | null> => {
    const docRef = doc(db, PROMOTIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    const promotion = convertFirestoreDataToPromotion(docSnap.id, docSnap.data());
    if (!promotionMatchesCurrentStoreScope(promotion)) {
      return null;
    }
    return promotion;
  },

  // Créer un nouveau code promo (ID fourni par le client)
  createPromotion: async (id: string, promotion: NewPromotion): Promise<void> => {
    const docRef = doc(db, PROMOTIONS_COLLECTION, id);
    const currentStoreId = adminScopeService.getCurrentStoreId();
    const existingPromotion = await promotionService.getPromotionById(id);

    if (existingPromotion) {
      throw new Error(`Le code promo "${id}" existe déjà pour ce magasin.`);
    }

    const existingDoc = await getDoc(docRef);
    if (existingDoc.exists()) {
      throw new Error(
        `Le code promo "${id}" existe déjà sur un autre scope. Utilise un code différent pour éviter tout écrasement.`
      );
    }
    
    // Nettoyer les champs undefined pour Firestore
    const cleanedPromotion: any = {
      scopeType: promotion.scopeType ?? (currentStoreId ? 'store' : 'global'),
      scopeId: promotion.scopeId ?? currentStoreId ?? undefined,
      usedCountTotal: 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    // Ajouter seulement les champs qui ne sont pas undefined
    Object.keys(promotion).forEach(key => {
      const value = (promotion as any)[key];
      if (value !== undefined) {
        cleanedPromotion[key] = value;
      }
    });
    
    await setDoc(docRef, cleanedPromotion);
  },

  // Mettre à jour un code promo existant
  updatePromotion: async (id: string, promotion: Partial<NewPromotion>): Promise<void> => {
    const docRef = doc(db, PROMOTIONS_COLLECTION, id);
    const currentStoreId = adminScopeService.getCurrentStoreId();
    assertPromotionInCurrentStoreScope(
      await promotionService.getPromotionById(id),
      id,
      'mise à jour'
    );
    
    // Nettoyer les champs undefined pour Firestore
    const cleanedPromotion: any = {
      updatedAt: Timestamp.now()
    };
    
    // Ajouter seulement les champs qui ne sont pas undefined
    Object.keys(promotion).forEach(key => {
      const value = (promotion as any)[key];
      if (value !== undefined) {
        cleanedPromotion[key] = value;
      }
    });

    if (!('scopeType' in cleanedPromotion) && currentStoreId) {
      cleanedPromotion.scopeType = 'store';
      cleanedPromotion.scopeId = currentStoreId;
    }
    
    await updateDoc(docRef, cleanedPromotion);
  },

  // Supprimer un code promo
  deletePromotion: async (id: string): Promise<void> => {
    assertPromotionInCurrentStoreScope(
      await promotionService.getPromotionById(id),
      id,
      'suppression'
    );
    const docRef = doc(db, PROMOTIONS_COLLECTION, id);
    await deleteDoc(docRef);
  }
};

export default promotionService;
